import { mdiClose } from './icons';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { toasts } from '../state';
import { Svg } from './Icon';

export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ComponentChildren;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current!;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return (
    <dialog
      ref={ref}
      class={`modal${wide ? ' modal-wide' : ''}`}
      onClose={onClose}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog element itself.
        if (e.target === ref.current) onClose();
      }}
    >
      <div class="modal-body">
        <header class="modal-header">
          <h2>{title}</h2>
          <button type="button" class="icon-button" onClick={onClose} aria-label="Close">
            <Svg path={mdiClose} />
          </button>
        </header>
        {children}
      </div>
    </dialog>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ComponentChildren;
  children: ComponentChildren;
}) {
  return (
    <label class="field">
      <span class="field-label">{label}</span>
      {children}
      {hint && <span class="field-hint">{hint}</span>}
    </label>
  );
}

export function Toasts() {
  return (
    <div class="toasts" role="status" aria-live="polite">
      {toasts.value.map((t) => (
        <div key={t.id} class={`toast toast-${t.kind}`}>
          {t.message}
        </div>
      ))}
    </div>
  );
}

export function Spinner() {
  return <div class="spinner" role="progressbar" aria-label="Loading" />;
}

/** Copyable one-time link (invites, recovery). */
export function CopyLink({ url }: { url: string }) {
  return (
    <div class="copy-link">
      <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
      <button
        type="button"
        class="button"
        onClick={() => navigator.clipboard?.writeText(url)}
      >
        Copy
      </button>
    </div>
  );
}

export function formatDate(iso: string | null): string {
  if (!iso) return 'never';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso)
  );
}
